// src/pages/admin/AdminSponsoredDeals.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  orderBy,
  query,
  updateDoc,
  doc,
  serverTimestamp,
  Timestamp,
  addDoc,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";

const PAGE_SIZE = 10;

const STATUS_OPTIONS = [
  { value: "all", label: "Tous les statuts" },
  { value: "pending", label: "En attente" },
  { value: "approved", label: "Approuvées" },
  { value: "active", label: "Actives" },
  { value: "rejected", label: "Refusées" },
  { value: "expired", label: "Expirées" },
];

const PAYMENT_OPTIONS = [
  { value: "all", label: "Tous les paiements" },
  { value: "pending", label: "Paiement en attente" },
  { value: "paid", label: "Payées" },
  { value: "failed", label: "Échouées" },
  { value: "not_required", label: "Non requis" },
];

const PLACEMENT_OPTIONS = [
  { value: "homepage_featured_shops", label: "Homepage · Boutiques sponsorisées" },
  { value: "homepage_products", label: "Homepage · Produits sponsorisés" },
  { value: "category_top", label: "Haut de catégorie" },
  { value: "search_top", label: "Résultats de recherche" },
  { value: "homepage_banner", label: "Bannière homepage" },
];

function formatMoney(v) {
  const n = Number(v || 0);
  return `${n.toLocaleString("fr-FR")} FCFA`;
}

function normalizeText(str = "") {
  return String(str)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function statusLabel(status) {
  const s = String(status || "").toLowerCase();
  if (s === "pending") return "En attente";
  if (s === "approved") return "Approuvée";
  if (s === "active") return "Active";
  if (s === "rejected") return "Refusée";
  if (s === "expired") return "Expirée";
  return status || "—";
}

function paymentLabel(status) {
  const s = String(status || "").toLowerCase();
  if (s === "pending") return "Paiement en attente";
  if (s === "paid") return "Payée";
  if (s === "failed") return "Paiement échoué";
  if (s === "not_required") return "Non requis";
  return status || "—";
}

function badgeClass(type, value) {
  const v = String(value || "").toLowerCase();

  if (type === "status") {
    if (v === "active" || v === "approved") return "asd-badge asd-badge-success";
    if (v === "pending") return "asd-badge asd-badge-warning";
    if (v === "rejected" || v === "expired") return "asd-badge asd-badge-danger";
  }

  if (type === "payment") {
    if (v === "paid" || v === "not_required") return "asd-badge asd-badge-success";
    if (v === "pending") return "asd-badge asd-badge-warning";
    if (v === "failed") return "asd-badge asd-badge-danger";
  }

  return "asd-badge asd-badge-default";
}

function toDateInputValue(value) {
  if (!value) return "";
  try {
    const d =
      typeof value?.toDate === "function" ? value.toDate() : new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  } catch {
    return "";
  }
}

function formatDateSafe(value) {
  if (!value) return "—";
  try {
    const d =
      typeof value?.toDate === "function" ? value.toDate() : new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("fr-FR");
  } catch {
    return "—";
  }
}

function computeEndDate(startAtStr, durationDays) {
  if (!startAtStr) return null;
  const start = new Date(`${startAtStr}T00:00:00`);
  if (Number.isNaN(start.getTime())) return null;
  const end = new Date(start);
  end.setDate(end.getDate() + Math.max(1, Number(durationDays || 1)) - 1);
  return end;
}

function calcCTR(clicks, impressions) {
  const c = Number(clicks || 0);
  const i = Number(impressions || 0);
  if (!i) return "0%";
  return `${((c / i) * 100).toFixed(2)}%`;
}

function calcConversionRate(conversions, clicks) {
  const conv = Number(conversions || 0);
  const c = Number(clicks || 0);
  if (!c) return "0%";
  return `${((conv / c) * 100).toFixed(2)}%`;
}

export default function AdminSponsoredDeals() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  const [previewDeal, setPreviewDeal] = useState(null);
  const [editMap, setEditMap] = useState({});

  const loadDeals = async () => {
    setLoading(true);
    setError("");

    try {
      const qRef = query(
        collection(db, "vendorSponsoredDeals"),
        orderBy("__name__", "desc")
      );
      const snap = await getDocs(qRef);
      const items = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      setDeals(items);

      setEditMap((prev) => {
        const next = { ...prev };
        items.forEach((deal) => {
          if (!next[deal.id]) {
            next[deal.id] = {
              amountApproved: deal.amountApproved ?? deal.amountRequested ?? 0,
              placement: deal.targetPlacement || "homepage_products",
              priority: deal.priority ?? 10,
              startAt: toDateInputValue(deal.startAt),
              adminNote: deal.adminNote || "",
            };
          }
        });
        return next;
      });
    } catch (err) {
      console.error("Erreur chargement deals sponsorisés :", err);
      setError("Impossible de charger les campagnes sponsorisées.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDeals();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, paymentFilter]);

  const handleEditField = (dealId, field, value) => {
    setEditMap((prev) => ({
      ...prev,
      [dealId]: {
        ...prev[dealId],
        [field]: value,
      },
    }));
  };

  const createSponsoredPlacement = async (deal, customData = {}) => {
    const placement = customData.placement || deal.targetPlacement || "homepage_products";
    const startAt = customData.startAt || deal.startAt || null;
    const endAt = customData.endAt || deal.endAt || null;
    const priority = Number(customData.priority ?? deal.priority ?? 10);

    const existingSnap = await getDocs(
      query(
        collection(db, "sponsoredPlacements"),
        where("sourceDealId", "==", deal.id)
      )
    );

    const payload = {
      placement,
      priority,
      isActive: true,
      startAt: startAt || null,
      endAt: endAt || null,
      title: deal.title || "Campagne sponsorisée",
      subtitle:
        deal.type === "shop"
          ? "Boutique sponsorisée"
          : deal.type === "product"
          ? "Produit sponsorisé"
          : "Campagne premium",
      productIds: deal.productIds || [],
      categoryId: deal.categoryId || null,
      vendorId: deal.vendorId || null,
      vendorName: deal.vendorName || null,
      imageUrl: deal.imageUrl || null,
      bannerDesktopUrl: deal.bannerDesktopUrl || null,
      bannerMobileUrl: deal.bannerMobileUrl || null,
      redirectUrl:
        deal.type === "shop"
          ? `/shop/${deal.vendorId || ""}`
          : deal.type === "product" && Array.isArray(deal.productIds) && deal.productIds[0]
          ? `/p/${deal.productIds[0]}`
          : "/",
      impressions: Number(deal.impressions || 0),
      clicks: Number(deal.clicks || 0),
      conversions: Number(deal.conversions || 0),
      updatedAt: serverTimestamp(),
    };

    if (!existingSnap.empty) {
      const existingDoc = existingSnap.docs[0];
      await updateDoc(doc(db, "sponsoredPlacements", existingDoc.id), payload);
      return;
    }

    await addDoc(collection(db, "sponsoredPlacements"), {
      sourceDealId: deal.id,
      type: deal.type || "product",
      createdAt: serverTimestamp(),
      ...payload,
    });
  };

  const handleApprove = async (deal) => {
    setActionLoadingId(deal.id);
    setError("");
    setInfo("");

    try {
      const edit = editMap[deal.id] || {};
      const amountApproved = Number(edit.amountApproved || 0);
      const placement = edit.placement || deal.targetPlacement || "homepage_products";
      const priority = Number(edit.priority ?? 10);
      const adminNote = edit.adminNote?.trim() || null;
      const startAtStr = edit.startAt || "";
      const endDate = computeEndDate(startAtStr, deal.durationDays || 1);

      await updateDoc(doc(db, "vendorSponsoredDeals", deal.id), {
        amountApproved,
        targetPlacement: placement,
        priority,
        adminNote,
        status: "approved",
        paymentStatus:
          String(deal.paymentStatus || "").toLowerCase() === "paid"
            ? "paid"
            : "pending",
        startAt: startAtStr
          ? Timestamp.fromDate(new Date(`${startAtStr}T00:00:00`))
          : null,
        endAt: endDate ? Timestamp.fromDate(endDate) : null,
        updatedAt: serverTimestamp(),
      });

      setInfo("Campagne approuvée avec succès.");
      await loadDeals();
    } catch (err) {
      console.error("Erreur approbation deal :", err);
      setError("Impossible d’approuver cette campagne.");
    } finally {
      setActionLoadingId("");
    }
  };

  const handleReject = async (deal) => {
    const confirmReject = window.confirm(
      `Refuser la campagne "${deal.title || "sans titre"}" ?`
    );
    if (!confirmReject) return;

    setActionLoadingId(deal.id);
    setError("");
    setInfo("");

    try {
      const edit = editMap[deal.id] || {};
      await updateDoc(doc(db, "vendorSponsoredDeals", deal.id), {
        status: "rejected",
        adminNote: edit.adminNote?.trim() || deal.adminNote || null,
        updatedAt: serverTimestamp(),
      });

      setInfo("Campagne refusée.");
      await loadDeals();
    } catch (err) {
      console.error("Erreur refus deal :", err);
      setError("Impossible de refuser cette campagne.");
    } finally {
      setActionLoadingId("");
    }
  };

  const handleMarkPaid = async (deal) => {
    setActionLoadingId(deal.id);
    setError("");
    setInfo("");

    try {
      const edit = editMap[deal.id] || {};
      const startAtStr = edit.startAt || toDateInputValue(deal.startAt);
      const placement = edit.placement || deal.targetPlacement || "homepage_products";
      const priority = Number(edit.priority ?? deal.priority ?? 10);
      const amountApproved = Number(
        edit.amountApproved ?? deal.amountApproved ?? deal.amountRequested ?? 0
      );

      const endDate = computeEndDate(startAtStr, deal.durationDays || 1);

      const startAtValue = startAtStr
        ? Timestamp.fromDate(new Date(`${startAtStr}T00:00:00`))
        : deal.startAt || null;

      const endAtValue = endDate ? Timestamp.fromDate(endDate) : deal.endAt || null;

      await updateDoc(doc(db, "vendorSponsoredDeals", deal.id), {
        paymentStatus: "paid",
        status: "active",
        amountApproved,
        targetPlacement: placement,
        priority,
        startAt: startAtValue,
        endAt: endAtValue,
        updatedAt: serverTimestamp(),
      });

      await createSponsoredPlacement(
        {
          ...deal,
          paymentStatus: "paid",
          status: "active",
          amountApproved,
          targetPlacement: placement,
          priority,
          startAt: startAtValue,
          endAt: endAtValue,
        },
        {
          placement,
          priority,
          startAt: startAtValue,
          endAt: endAtValue,
        }
      );

      setInfo("Paiement validé et campagne activée.");
      await loadDeals();
    } catch (err) {
      console.error("Erreur validation paiement :", err);
      setError("Impossible de marquer cette campagne comme payée.");
    } finally {
      setActionLoadingId("");
    }
  };

  const handleActivate = async (deal) => {
    setActionLoadingId(deal.id);
    setError("");
    setInfo("");

    try {
      const edit = editMap[deal.id] || {};
      const startAtStr = edit.startAt || toDateInputValue(deal.startAt);
      const placement = edit.placement || deal.targetPlacement || "homepage_products";
      const priority = Number(edit.priority ?? deal.priority ?? 10);
      const endDate = computeEndDate(startAtStr, deal.durationDays || 1);

      const startAtValue = startAtStr
        ? Timestamp.fromDate(new Date(`${startAtStr}T00:00:00`))
        : deal.startAt || null;

      const endAtValue = endDate ? Timestamp.fromDate(endDate) : deal.endAt || null;

      await updateDoc(doc(db, "vendorSponsoredDeals", deal.id), {
        status: "active",
        targetPlacement: placement,
        priority,
        startAt: startAtValue,
        endAt: endAtValue,
        updatedAt: serverTimestamp(),
      });

      await createSponsoredPlacement(
        {
          ...deal,
          status: "active",
          targetPlacement: placement,
          priority,
          startAt: startAtValue,
          endAt: endAtValue,
        },
        {
          placement,
          priority,
          startAt: startAtValue,
          endAt: endAtValue,
        }
      );

      setInfo("Campagne activée.");
      await loadDeals();
    } catch (err) {
      console.error("Erreur activation campagne :", err);
      setError("Impossible d’activer cette campagne.");
    } finally {
      setActionLoadingId("");
    }
  };

  const handleExpire = async (deal) => {
    const confirmExpire = window.confirm(
      `Marquer la campagne "${deal.title || "sans titre"}" comme expirée ?`
    );
    if (!confirmExpire) return;

    setActionLoadingId(deal.id);
    setError("");
    setInfo("");

    try {
      await updateDoc(doc(db, "vendorSponsoredDeals", deal.id), {
        status: "expired",
        updatedAt: serverTimestamp(),
      });

      const existingSnap = await getDocs(
        query(
          collection(db, "sponsoredPlacements"),
          where("sourceDealId", "==", deal.id)
        )
      );

      for (const d of existingSnap.docs) {
        await updateDoc(doc(db, "sponsoredPlacements", d.id), {
          isActive: false,
          updatedAt: serverTimestamp(),
        });
      }

      setInfo("Campagne expirée et placement désactivé.");
      await loadDeals();
    } catch (err) {
      console.error("Erreur expiration campagne :", err);
      setError("Impossible de clôturer cette campagne.");
    } finally {
      setActionLoadingId("");
    }
  };

  const filteredDeals = useMemo(() => {
    const needle = normalizeText(search);

    return deals.filter((deal) => {
      const statusOk =
        statusFilter === "all" ? true : String(deal.status || "") === statusFilter;

      const paymentOk =
        paymentFilter === "all"
          ? true
          : String(deal.paymentStatus || "") === paymentFilter;

      if (!statusOk || !paymentOk) return false;
      if (!needle) return true;

      const haystack = normalizeText(
        [
          deal.vendorName,
          deal.vendorEmail,
          deal.title,
          deal.description,
          deal.type,
          deal.targetPlacement,
          deal.objective,
          deal.status,
          deal.paymentStatus,
          deal.adminNote,
        ]
          .filter(Boolean)
          .join(" ")
      );

      return haystack.includes(needle);
    });
  }, [deals, search, statusFilter, paymentFilter]);

  const stats = useMemo(() => {
    return {
      total: deals.length,
      pending: deals.filter((d) => d.status === "pending").length,
      approved: deals.filter((d) => d.status === "approved").length,
      active: deals.filter((d) => d.status === "active").length,
      rejected: deals.filter((d) => d.status === "rejected").length,
      paid: deals.filter((d) => d.paymentStatus === "paid").length,
      impressions: deals.reduce((sum, d) => sum + Number(d.impressions || 0), 0),
      clicks: deals.reduce((sum, d) => sum + Number(d.clicks || 0), 0),
      conversions: deals.reduce((sum, d) => sum + Number(d.conversions || 0), 0),
    };
  }, [deals]);

  const totalPages = Math.max(1, Math.ceil(filteredDeals.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const start = (safePage - 1) * PAGE_SIZE;
  const end = start + PAGE_SIZE;
  const visibleDeals = filteredDeals.slice(start, end);

  return (
    <div className="asd-page">
      <main className="asd-main">
        <header className="asd-header">
          <div>
            <h1 className="asd-title">Campagnes sponsorisées vendeurs</h1>
            <p className="asd-subtitle">
              Gérez les deals commerciaux entre GoShop et les boutiques pour la
              mise en avant, la visibilité et les ventes sponsorisées.
            </p>
          </div>

          <button
            type="button"
            className="asd-refreshBtn"
            onClick={loadDeals}
            disabled={loading}
          >
            {loading ? "Chargement..." : "Actualiser"}
          </button>
        </header>

        {error && <div className="asd-alert asd-alert-error">{error}</div>}
        {info && <div className="asd-alert asd-alert-success">{info}</div>}

        <section className="asd-stats">
          <div className="asd-statCard">
            <span>Total</span>
            <strong>{stats.total}</strong>
          </div>
          <div className="asd-statCard asd-statCard-pending">
            <span>En attente</span>
            <strong>{stats.pending}</strong>
          </div>
          <div className="asd-statCard asd-statCard-approved">
            <span>Approuvées</span>
            <strong>{stats.approved}</strong>
          </div>
          <div className="asd-statCard asd-statCard-active">
            <span>Actives</span>
            <strong>{stats.active}</strong>
          </div>
          <div className="asd-statCard asd-statCard-rejected">
            <span>Refusées</span>
            <strong>{stats.rejected}</strong>
          </div>
          <div className="asd-statCard asd-statCard-paid">
            <span>Payées</span>
            <strong>{stats.paid}</strong>
          </div>
          <div className="asd-statCard">
            <span>Impressions</span>
            <strong>{stats.impressions}</strong>
          </div>
          <div className="asd-statCard">
            <span>Clics</span>
            <strong>{stats.clicks}</strong>
          </div>
          <div className="asd-statCard">
            <span>Conversions</span>
            <strong>{stats.conversions}</strong>
          </div>
        </section>

        <section className="asd-toolbarCard">
          <div className="asd-toolbar">
            <input
              type="search"
              className="asd-search"
              placeholder="Rechercher vendeur, campagne, objectif..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <select
              className="asd-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <select
              className="asd-filter"
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value)}
            >
              {PAYMENT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="asd-toolbarMeta">
            {filteredDeals.length} campagne(s) trouvée(s)
          </div>
        </section>

        <section className="asd-tableCard">
          {loading ? (
            <p className="asd-empty">Chargement des campagnes…</p>
          ) : filteredDeals.length === 0 ? (
            <p className="asd-empty">Aucune campagne sponsorisée trouvée.</p>
          ) : (
            <>
              <div className="asd-list">
                {visibleDeals.map((deal) => {
                  const edit = editMap[deal.id] || {};
                  const isBusy = actionLoadingId === deal.id;

                  const impressions = Number(deal.impressions || 0);
                  const clicks = Number(deal.clicks || 0);
                  const conversions = Number(deal.conversions || 0);

                  return (
                    <article key={deal.id} className="asd-item">
                      <div className="asd-item-top">
                        <div className="asd-item-main">
                          <div className="asd-item-titleRow">
                            <h2 className="asd-item-title">
                              {deal.title || "Campagne sans titre"}
                            </h2>
                            <span className={badgeClass("status", deal.status)}>
                              {statusLabel(deal.status)}
                            </span>
                            <span className={badgeClass("payment", deal.paymentStatus)}>
                              {paymentLabel(deal.paymentStatus)}
                            </span>
                          </div>

                          <div className="asd-item-meta">
                            <span><strong>Vendeur :</strong> {deal.vendorName || "—"}</span>
                            <span><strong>Type :</strong> {deal.type || "—"}</span>
                            <span><strong>Objectif :</strong> {deal.objective || "—"}</span>
                          </div>

                          <div className="asd-item-meta">
                            <span><strong>Budget demandé :</strong> {formatMoney(deal.amountRequested || 0)}</span>
                            <span>
                              <strong>Budget approuvé :</strong>{" "}
                              {deal.amountApproved != null ? formatMoney(deal.amountApproved) : "—"}
                            </span>
                            <span><strong>Durée :</strong> {deal.durationDays || 0} jour(s)</span>
                          </div>

                          <div className="asd-item-meta">
                            <span><strong>Début :</strong> {formatDateSafe(deal.startAt)}</span>
                            <span><strong>Fin :</strong> {formatDateSafe(deal.endAt)}</span>
                            <span><strong>Placement :</strong> {deal.targetPlacement || "—"}</span>
                          </div>

                          <div className="asd-trackRow">
                            <div className="asd-trackCard">
                              <span>Impressions</span>
                              <strong>{impressions}</strong>
                            </div>
                            <div className="asd-trackCard">
                              <span>Clics</span>
                              <strong>{clicks}</strong>
                            </div>
                            <div className="asd-trackCard">
                              <span>Conversions</span>
                              <strong>{conversions}</strong>
                            </div>
                            <div className="asd-trackCard">
                              <span>CTR</span>
                              <strong>{calcCTR(clicks, impressions)}</strong>
                            </div>
                            <div className="asd-trackCard">
                              <span>Taux conversion</span>
                              <strong>{calcConversionRate(conversions, clicks)}</strong>
                            </div>
                          </div>

                          {deal.description && (
                            <p className="asd-item-desc">{deal.description}</p>
                          )}
                        </div>

                        <button
                          type="button"
                          className="asd-previewBtn"
                          onClick={() => setPreviewDeal(deal)}
                        >
                          Aperçu
                        </button>
                      </div>

                      <div className="asd-editGrid">
                        <div className="asd-field">
                          <label>Montant approuvé</label>
                          <input
                            type="number"
                            min="0"
                            value={edit.amountApproved ?? ""}
                            onChange={(e) =>
                              handleEditField(deal.id, "amountApproved", e.target.value)
                            }
                          />
                        </div>

                        <div className="asd-field">
                          <label>Placement</label>
                          <select
                            value={edit.placement || "homepage_products"}
                            onChange={(e) =>
                              handleEditField(deal.id, "placement", e.target.value)
                            }
                          >
                            {PLACEMENT_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="asd-field">
                          <label>Priorité</label>
                          <input
                            type="number"
                            min="1"
                            value={edit.priority ?? 10}
                            onChange={(e) =>
                              handleEditField(deal.id, "priority", e.target.value)
                            }
                          />
                        </div>

                        <div className="asd-field">
                          <label>Date de début</label>
                          <input
                            type="date"
                            value={edit.startAt || ""}
                            onChange={(e) =>
                              handleEditField(deal.id, "startAt", e.target.value)
                            }
                          />
                        </div>

                        <div className="asd-field asd-field-full">
                          <label>Note admin</label>
                          <textarea
                            rows={2}
                            value={edit.adminNote || ""}
                            onChange={(e) =>
                              handleEditField(deal.id, "adminNote", e.target.value)
                            }
                            placeholder="Ex : pack validé, bannière à fournir, ciblage conseillé..."
                          />
                        </div>
                      </div>

                      <div className="asd-actions">
                        <button
                          type="button"
                          className="asd-btn asd-btn-outline"
                          disabled={isBusy}
                          onClick={() => handleReject(deal)}
                        >
                          {isBusy ? "Traitement..." : "Refuser"}
                        </button>

                        <button
                          type="button"
                          className="asd-btn asd-btn-secondary"
                          disabled={isBusy}
                          onClick={() => handleApprove(deal)}
                        >
                          {isBusy ? "Traitement..." : "Approuver"}
                        </button>

                        <button
                          type="button"
                          className="asd-btn asd-btn-primary"
                          disabled={isBusy}
                          onClick={() => handleMarkPaid(deal)}
                        >
                          {isBusy ? "Traitement..." : "Marquer payé & activer"}
                        </button>

                        <button
                          type="button"
                          className="asd-btn asd-btn-dark"
                          disabled={isBusy}
                          onClick={() => handleActivate(deal)}
                        >
                          {isBusy ? "Traitement..." : "Activer"}
                        </button>

                        <button
                          type="button"
                          className="asd-btn asd-btn-danger"
                          disabled={isBusy}
                          onClick={() => handleExpire(deal)}
                        >
                          {isBusy ? "Traitement..." : "Expirer"}
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>

              <div className="asd-pagination">
                <div className="asd-paginationInfo">
                  Affichage de <strong>{start + 1}</strong> à{" "}
                  <strong>{Math.min(end, filteredDeals.length)}</strong> sur{" "}
                  <strong>{filteredDeals.length}</strong>
                </div>

                <div className="asd-paginationControls">
                  <button
                    type="button"
                    className="asd-pageBtn"
                    disabled={safePage <= 1}
                    onClick={() => {
                      setCurrentPage((p) => Math.max(1, p - 1));
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                  >
                    ← Précédent
                  </button>

                  <div className="asd-pageCurrent">
                    Page {safePage} / {totalPages}
                  </div>

                  <button
                    type="button"
                    className="asd-pageBtn"
                    disabled={safePage >= totalPages}
                    onClick={() => {
                      setCurrentPage((p) => Math.min(totalPages, p + 1));
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                  >
                    Suivant →
                  </button>
                </div>
              </div>
            </>
          )}
        </section>

        {previewDeal && (
          <div
            className="asd-modalBackdrop"
            onMouseDown={() => setPreviewDeal(null)}
          >
            <div className="asd-modal" onMouseDown={(e) => e.stopPropagation()}>
              <div className="asd-modalHeader">
                <div>
                  <div className="asd-modalTitle">
                    {previewDeal.title || "Campagne sponsorisée"}
                  </div>
                  <div className="asd-modalSub">
                    {previewDeal.vendorName || "—"} • {previewDeal.type || "—"}
                  </div>
                </div>

                <button
                  type="button"
                  className="asd-closeBtn"
                  onClick={() => setPreviewDeal(null)}
                >
                  ✕
                </button>
              </div>

              <div className="asd-modalBody">
                <div className="asd-chipRow">
                  <span className="asd-chip">Statut : {statusLabel(previewDeal.status)}</span>
                  <span className="asd-chip">Paiement : {paymentLabel(previewDeal.paymentStatus)}</span>
                  <span className="asd-chip">
                    Budget : {formatMoney(previewDeal.amountApproved ?? previewDeal.amountRequested ?? 0)}
                  </span>
                </div>

                <div className="asd-trackRow asd-trackRow-modal">
                  <div className="asd-trackCard">
                    <span>Impressions</span>
                    <strong>{Number(previewDeal.impressions || 0)}</strong>
                  </div>
                  <div className="asd-trackCard">
                    <span>Clics</span>
                    <strong>{Number(previewDeal.clicks || 0)}</strong>
                  </div>
                  <div className="asd-trackCard">
                    <span>Conversions</span>
                    <strong>{Number(previewDeal.conversions || 0)}</strong>
                  </div>
                  <div className="asd-trackCard">
                    <span>CTR</span>
                    <strong>
                      {calcCTR(previewDeal.clicks, previewDeal.impressions)}
                    </strong>
                  </div>
                  <div className="asd-trackCard">
                    <span>Taux conversion</span>
                    <strong>
                      {calcConversionRate(previewDeal.conversions, previewDeal.clicks)}
                    </strong>
                  </div>
                </div>

                <div className="asd-sectionBlock">
                  <div className="asd-sectionTitle">Description</div>
                  <div className="asd-sectionText">{previewDeal.description || "—"}</div>
                </div>

                <div className="asd-sectionBlock">
                  <div className="asd-sectionTitle">Placement</div>
                  <div className="asd-sectionText">{previewDeal.targetPlacement || "—"}</div>
                </div>

                <div className="asd-sectionBlock">
                  <div className="asd-sectionTitle">Produits ciblés</div>
                  <div className="asd-sectionText">
                    {Array.isArray(previewDeal.productIds) && previewDeal.productIds.length > 0
                      ? previewDeal.productIds.join(", ")
                      : "—"}
                  </div>
                </div>

                {previewDeal.adminNote && (
                  <div className="asd-sectionBlock">
                    <div className="asd-sectionTitle">Note admin</div>
                    <div className="asd-sectionText">{previewDeal.adminNote}</div>
                  </div>
                )}

                <div className="asd-modalActions">
                  <button
                    type="button"
                    className="asd-lightBtn"
                    onClick={() => setPreviewDeal(null)}
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <style>{`
          .asd-page{
            min-height:100vh;
            background:linear-gradient(180deg,#f8fafc 0%, #f1f5f9 100%);
            padding:24px 16px 40px;
            display:flex;
            justify-content:center;
          }

          .asd-main{
            width:100%;
            max-width:1180px;
            font-size:14px;
            line-height:1.5;
          }

          .asd-header{
            display:flex;
            justify-content:space-between;
            align-items:flex-start;
            gap:16px;
            flex-wrap:wrap;
            margin-bottom:16px;
          }

          .asd-title{
            font-size:26px;
            font-weight:900;
            color:#0f172a;
            margin:0 0 6px;
          }

          .asd-subtitle{
            font-size:13px;
            color:#475569;
            margin:0;
            max-width:760px;
          }

          .asd-refreshBtn{
            display:inline-flex;
            align-items:center;
            justify-content:center;
            padding:10px 16px;
            border-radius:999px;
            border:1px solid #dbeafe;
            background:#eff6ff;
            color:#1d4ed8;
            font-size:13px;
            font-weight:800;
            cursor:pointer;
          }

          .asd-refreshBtn:hover:not(:disabled){
            background:#dbeafe;
          }

          .asd-alert{
            margin-bottom:12px;
            padding:10px 12px;
            border-radius:12px;
            font-size:13px;
            font-weight:700;
          }

          .asd-alert-error{
            background:#fef2f2;
            color:#b91c1c;
            border:1px solid #fecaca;
          }

          .asd-alert-success{
            background:#ecfdf3;
            color:#166534;
            border:1px solid #bbf7d0;
          }

          .asd-stats{
            display:grid;
            grid-template-columns:repeat(3, minmax(0,1fr));
            gap:12px;
            margin-bottom:16px;
          }

          .asd-statCard{
            background:#fff;
            border:1px solid #e5e7eb;
            border-radius:18px;
            padding:14px 16px;
            box-shadow:0 12px 28px rgba(15,23,42,.05);
          }

          .asd-statCard span{
            display:block;
            font-size:12px;
            color:#64748b;
            margin-bottom:6px;
          }

          .asd-statCard strong{
            font-size:24px;
            color:#0f172a;
            font-weight:900;
          }

          .asd-statCard-pending{background:linear-gradient(180deg,#fffef2 0%, #ffffff 100%);}
          .asd-statCard-approved{background:linear-gradient(180deg,#eff6ff 0%, #ffffff 100%);}
          .asd-statCard-active{background:linear-gradient(180deg,#f0fdf4 0%, #ffffff 100%);}
          .asd-statCard-rejected{background:linear-gradient(180deg,#fff1f2 0%, #ffffff 100%);}
          .asd-statCard-paid{background:linear-gradient(180deg,#ecfeff 0%, #ffffff 100%);}

          .asd-toolbarCard,
          .asd-tableCard{
            background:#ffffff;
            border-radius:22px;
            border:1px solid #e5e7eb;
            padding:14px;
            box-shadow:0 14px 34px rgba(15,23,42,.07);
          }

          .asd-toolbarCard{margin-bottom:14px;}

          .asd-toolbar{
            display:flex;
            justify-content:space-between;
            align-items:center;
            gap:10px;
            flex-wrap:wrap;
          }

          .asd-search,
          .asd-filter,
          .asd-field input,
          .asd-field select,
          .asd-field textarea{
            border:1px solid #d1d5db;
            border-radius:14px;
            background:#fff;
            padding:10px 12px;
            font-size:13px;
            outline:none;
          }

          .asd-search{
            min-width:320px;
            flex:1;
          }

          .asd-search:focus,
          .asd-filter:focus,
          .asd-field input:focus,
          .asd-field select:focus,
          .asd-field textarea:focus{
            border-color:#2563eb;
            box-shadow:0 0 0 3px rgba(37,99,235,.08);
          }

          .asd-toolbarMeta{
            margin-top:10px;
            font-size:12px;
            color:#64748b;
            font-weight:700;
          }

          .asd-list{
            display:flex;
            flex-direction:column;
            gap:14px;
          }

          .asd-item{
            border:1px solid #e5e7eb;
            border-radius:20px;
            padding:14px;
            background:#fff;
            box-shadow:0 10px 24px rgba(15,23,42,.04);
          }

          .asd-item-top{
            display:flex;
            justify-content:space-between;
            gap:14px;
            align-items:flex-start;
            margin-bottom:12px;
          }

          .asd-item-main{
            flex:1;
            min-width:0;
          }

          .asd-item-titleRow{
            display:flex;
            flex-wrap:wrap;
            gap:8px;
            align-items:center;
            margin-bottom:6px;
          }

          .asd-item-title{
            margin:0;
            font-size:18px;
            font-weight:900;
            color:#0f172a;
          }

          .asd-item-meta{
            display:flex;
            flex-wrap:wrap;
            gap:14px;
            font-size:12px;
            color:#64748b;
            margin-top:4px;
          }

          .asd-item-desc{
            margin:10px 0 0;
            font-size:13px;
            color:#475569;
          }

          .asd-previewBtn{
            display:inline-flex;
            align-items:center;
            justify-content:center;
            padding:8px 14px;
            border-radius:999px;
            border:1px solid #e5e7eb;
            background:#f8fafc;
            color:#111827;
            font-size:12px;
            font-weight:800;
            cursor:pointer;
            white-space:nowrap;
          }

          .asd-trackRow{
            display:grid;
            grid-template-columns:repeat(5, minmax(0,1fr));
            gap:10px;
            margin-top:12px;
          }

          .asd-trackCard{
            background:#f8fafc;
            border:1px solid #e5e7eb;
            border-radius:14px;
            padding:10px 12px;
          }

          .asd-trackCard span{
            display:block;
            font-size:11px;
            color:#64748b;
            margin-bottom:4px;
          }

          .asd-trackCard strong{
            font-size:15px;
            color:#0f172a;
            font-weight:900;
          }

          .asd-editGrid{
            display:grid;
            grid-template-columns:repeat(4, minmax(0,1fr));
            gap:12px;
            margin-top:12px;
          }

          .asd-field{
            display:flex;
            flex-direction:column;
            gap:6px;
          }

          .asd-field label{
            font-size:12px;
            color:#334155;
            font-weight:800;
          }

          .asd-field-full{
            grid-column:1 / -1;
          }

          .asd-actions{
            display:flex;
            flex-wrap:wrap;
            gap:8px;
            margin-top:14px;
          }

          .asd-btn{
            display:inline-flex;
            align-items:center;
            justify-content:center;
            padding:9px 14px;
            border-radius:999px;
            font-size:12px;
            font-weight:800;
            cursor:pointer;
            border:1px solid transparent;
          }

          .asd-btn-outline{
            background:#fff;
            color:#b91c1c;
            border-color:#fecaca;
          }

          .asd-btn-secondary{
            background:#eff6ff;
            color:#1d4ed8;
            border-color:#dbeafe;
          }

          .asd-btn-primary{
            background:#16a34a;
            color:#fff;
            border-color:#16a34a;
          }

          .asd-btn-dark{
            background:#111827;
            color:#fff;
            border-color:#111827;
          }

          .asd-btn-danger{
            background:#fff1f2;
            color:#b91c1c;
            border-color:#fecdd3;
          }

          .asd-btn:disabled{
            opacity:.6;
            cursor:not-allowed;
          }

          .asd-badge{
            display:inline-flex;
            align-items:center;
            padding:4px 10px;
            border-radius:999px;
            font-size:11px;
            font-weight:900;
          }

          .asd-badge-success{
            background:#dcfce7;
            color:#166534;
          }

          .asd-badge-warning{
            background:#fef9c3;
            color:#92400e;
          }

          .asd-badge-danger{
            background:#fee2e2;
            color:#b91c1c;
          }

          .asd-badge-default{
            background:#f1f5f9;
            color:#334155;
          }

          .asd-empty{
            font-size:13px;
            color:#6b7280;
            margin:0;
            padding:8px 0;
          }

          .asd-pagination{
            margin-top:16px;
            display:flex;
            justify-content:space-between;
            align-items:center;
            gap:12px;
            flex-wrap:wrap;
          }

          .asd-paginationInfo{
            font-size:12px;
            color:#64748b;
          }

          .asd-paginationControls{
            display:flex;
            align-items:center;
            gap:10px;
            flex-wrap:wrap;
          }

          .asd-pageBtn{
            border:1px solid #d1d5db;
            background:#fff;
            color:#111827;
            border-radius:999px;
            padding:8px 14px;
            font-size:12px;
            font-weight:800;
            cursor:pointer;
          }

          .asd-pageBtn:hover:not(:disabled){
            background:#f8fafc;
          }

          .asd-pageBtn:disabled{
            opacity:.5;
            cursor:not-allowed;
          }

          .asd-pageCurrent{
            font-size:12px;
            color:#374151;
            font-weight:800;
          }

          .asd-modalBackdrop{
            position:fixed;
            inset:0;
            background:rgba(2,6,23,.6);
            display:flex;
            align-items:center;
            justify-content:center;
            padding:16px;
            z-index:9999;
          }

          .asd-modal{
            width:min(900px, 100%);
            background:#fff;
            border-radius:22px;
            overflow:hidden;
            border:1px solid rgba(255,255,255,.15);
            box-shadow:0 30px 80px rgba(0,0,0,.35);
          }

          .asd-modalHeader{
            display:flex;
            justify-content:space-between;
            align-items:center;
            gap:12px;
            padding:14px 16px;
            border-bottom:1px solid #e5e7eb;
          }

          .asd-modalTitle{
            font-size:18px;
            font-weight:900;
            color:#0f172a;
          }

          .asd-modalSub{
            font-size:12px;
            color:#64748b;
            margin-top:3px;
          }

          .asd-closeBtn{
            width:38px;
            height:38px;
            border-radius:12px;
            border:1px solid #e5e7eb;
            background:#f8fafc;
            cursor:pointer;
            font-weight:900;
          }

          .asd-modalBody{
            padding:16px;
          }

          .asd-chipRow{
            display:flex;
            gap:8px;
            flex-wrap:wrap;
            margin-bottom:14px;
          }

          .asd-chip{
            display:inline-flex;
            align-items:center;
            padding:5px 10px;
            border-radius:999px;
            background:#f1f5f9;
            color:#0f172a;
            border:1px solid #e2e8f0;
            font-size:12px;
            font-weight:900;
          }

          .asd-trackRow-modal{
            margin-bottom:12px;
          }

          .asd-sectionBlock{
            margin-top:12px;
          }

          .asd-sectionTitle{
            font-size:13px;
            font-weight:900;
            color:#0f172a;
            margin-bottom:4px;
          }

          .asd-sectionText{
            font-size:13px;
            color:#475569;
          }

          .asd-modalActions{
            margin-top:18px;
            display:flex;
            justify-content:flex-end;
          }

          .asd-lightBtn{
            display:inline-flex;
            align-items:center;
            justify-content:center;
            padding:9px 14px;
            border-radius:999px;
            border:1px solid #e5e7eb;
            background:#f8fafc;
            color:#111827;
            font-size:13px;
            font-weight:800;
            cursor:pointer;
          }

          @media (max-width: 1100px){
            .asd-stats{
              grid-template-columns:repeat(3, minmax(0,1fr));
            }

            .asd-editGrid{
              grid-template-columns:repeat(2, minmax(0,1fr));
            }

            .asd-trackRow{
              grid-template-columns:repeat(2, minmax(0,1fr));
            }
          }

          @media (max-width: 760px){
            .asd-stats{
              grid-template-columns:repeat(2, minmax(0,1fr));
            }

            .asd-search{
              min-width:100%;
            }

            .asd-toolbar{
              flex-direction:column;
              align-items:stretch;
            }

            .asd-item-top{
              flex-direction:column;
            }

            .asd-pagination{
              flex-direction:column;
              align-items:flex-start;
            }
          }

          @media (max-width: 560px){
            .asd-stats{
              grid-template-columns:minmax(0,1fr);
            }

            .asd-editGrid{
              grid-template-columns:minmax(0,1fr);
            }

            .asd-trackRow{
              grid-template-columns:minmax(0,1fr);
            }
          }
        `}</style>
      </main>
    </div>
  );
}
